import React, { useState, useCallback, Suspense, lazy } from 'react';
import { MapPin } from 'lucide-react';
import PropertyMapList from '../../map/components/PropertyMapList';
import { BaseProperty } from '@/features/property/types';
import { Competitor } from '@/features/compset/types';
import { RealtimeProperty } from '../../realtimeProperty/types';
import NoAvailabilityMessage from '@/features/property/components/NoAvailabilityMessage';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/designSystem/Dialog';

// @ts-ignore
const PropertyMap = lazy(() => import('./PropertyMap'));

interface MapModalProps {
  isOpen: boolean;
  onClose: () => void;
  ownProperty: RealtimeProperty;
  competitors: Competitor[];
  marketProperties: BaseProperty[];
}

const MapLoadingPlaceholder: React.FC = () => (
  <div className="flex items-center justify-center h-full w-full bg-gray-50 rounded-lg border border-gray-200">
    <div className="text-center">
      <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
      <p className="text-gray-500">Loading map view...</p>
    </div>
  </div>
);

export const MapModal: React.FC<MapModalProps> = ({
  isOpen,
  onClose,
  ownProperty,
  competitors,
  marketProperties,
}) => {
  const [selectedProperty, setSelectedProperty] = useState<BaseProperty | Competitor | undefined>(undefined);

  const handlePropertyClick = useCallback((property: BaseProperty | Competitor): void => {
    setSelectedProperty(property);
  }, []);

  const hasValidData = ownProperty !== null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl w-full h-[90vh] flex flex-col overflow-hidden p-0">
        {/* Modal Header */}
        <div className="bg-blue-50 p-4 border-b border-blue-100 flex-shrink-0">
          <DialogHeader>
            <DialogTitle className="flex items-center text-left">
              <MapPin className="w-5 h-5 mr-2 text-blue-600" />
              Competitor Analysis
            </DialogTitle>
            <DialogDescription className="ml-7 text-left">
              See what your users see on booking.com search map & what your properties competitors are.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-hidden">
          {hasValidData ? (
            <div className="flex flex-col lg:flex-row h-full gap-4 p-4">
              <div className="w-full h-[30vh] lg:w-1/3 lg:h-full mb-4 lg:mb-0">
                <PropertyMapList
                  ownProperty={ownProperty}
                  competitors={competitors}
                  marketProperties={marketProperties}
                  onPropertyClick={handlePropertyClick}
                  selectedPropertyId={selectedProperty?.property_id}
                />
              </div>

              <div className="w-full h-[70vh] lg:w-2/3 lg:h-full">
                <Suspense fallback={<MapLoadingPlaceholder />}>
                  <PropertyMap
                    ownProperty={ownProperty}
                    competitors={competitors}
                    marketProperties={marketProperties}
                    onPropertyClick={handlePropertyClick}
                    selectedPropertyId={selectedProperty?.property_id}
                  />
                </Suspense>
              </div>
            </div>
          ) : (
            <div className="p-4">
              <NoAvailabilityMessage />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};